import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { distinctUntilChanged, take } from 'rxjs';
import { AddNewLocationDialogComponent } from '../add-new-location-dialog/add-new-location-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { OrderLocations } from 'src/app/utils/systemEnums';
import { countries } from 'src/@order/commonData/commonUiData';
import { LocationPointType } from 'src/@order/commonData/location-point-type';
import { WebDestinationsDialogComponent } from '../web-destinations-dialog/web-destinations-dialog.component';
import { AuthServiceLocal } from 'src/@order/services/auth.service';

@Component({
  selector: 'app-pickup-locations-dialog',
  templateUrl: './pickup-locations-dialog.component.html',
  styleUrls: ['./pickup-locations-dialog.component.scss'],
})
export class PickupLocationsDialogComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() heading: string;
  @Input() isPickup: number;
  @Input() selected: any;
  @Input() isHeavyItemAndRelocateOrder: boolean;
  @Input() isInterRegional: boolean;
  @Input() isStandardOrder: boolean;
  @Input() groupIndex: any;
  @Input() isShowTrustCheck: boolean;
  @Output() close = new EventEmitter<void>();
  @Output() locationSelected = new EventEmitter<any>();

  locations: any[] = [];
  countries = countries;
  selectedCountryCode: string;
  customerData: any;
  selectedDropoffs: string[] = [];

  constructor(
    // private _dataService: any,
    private _matDialog: MatDialog,
    private afs: AngularFirestore,
    private _auth: AuthServiceLocal
  ) {
    this.customerData = JSON.parse(localStorage.getItem('authUser') || '{}');

    this.countries.forEach((country) => {
      if (country.title === localStorage.getItem('country')) {
        country.selected = 1;
        this.selectedCountryCode = country.code;
      } else {
        country.selected = 0;
      }
    });
  }

  ngOnInit() {
    this.setSelectedDropoff();
    this.loadLocations();

    console.log('this.isShowTrustCheck', this.isShowTrustCheck);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isPickup'] && !changes['isPickup'].firstChange) {
      this.loadLocations();
    }
    if (changes['selected']) {
      this.setSelectedDropoff();
    }
  }

  setSelectedDropoff() {
    this.selectedDropoffs = [];

    if (!this.isPickup) {
      // Handle array of dropoff points
      if (Array.isArray(this.selected)) {
        // Always show all selected dropoffs with borders
        this.selected.forEach((item) => {
          const locationId = item?.dropoffPoint?.locationId;
          if (locationId && !this.selectedDropoffs.includes(locationId)) {
            this.selectedDropoffs.push(locationId);
          }
        });
      } else {
        // Handle single dropoff point (backward compatibility)
        const locationId = this.selected?.dropoffPoint?.locationId;
        if (locationId) {
          this.selectedDropoffs.push(locationId);
        }
      }
    } else if (this.isPickup && this.selected?.locationId) {
      this.selectedDropoffs = [this.selected.locationId];
    }
  }

  loadLocations() {
    this.afs
      .collection(OrderLocations, (ref) =>
        ref
          .where('userID', '==', Number(this.customerData.id))
          .where('countryCode', '==', String(this.selectedCountryCode))
          .orderBy('updateAt', 'desc')
      )
      .valueChanges()
      .pipe(distinctUntilChanged())
      .subscribe(
        (pointLocation: any[]) => {
          this.locations =
            pointLocation
              ?.filter(
                (item: LocationPointType) => !!item.isPickup === !!this.isPickup
              )
              .map((loc: any) => ({
                ...loc,
                placeId: loc?.placeId || loc?.placeID || loc?.place_id || '',
              })) || [];
          try {
            // console.log(
            //   '[PickupLocationsDialog] loadLocations:normalized',
            //   this.locations?.map((l) => ({
            //     id: l?.id,
            //     locationId: l?.locationId,
            //     placeId: l?.placeId,
            //   }))
            // );
          } catch {}
        },
        (error) => {
          console.error('Error getting documents: ', error);
          this.locations = [];
        }
      );
  }

  onNewLocation() {
    const dialogRef = this._matDialog.open(AddNewLocationDialogComponent, {
      panelClass: ['commonDialogWrapper'],
      backdropClass: 'dialogBackdrop',
      autoFocus: false,
      width: '610px',
      data: {
        heading: this.isPickup ? 'Pickup Location' : 'Drop Off Location',
        isPickup: this.isPickup,
        location: null,
        selected: this.selected || null,
        groupIndex: this.groupIndex,
        isInterRegional: this.isInterRegional,
        isHeavyItemAndRelocateOrder: this.isHeavyItemAndRelocateOrder,
        isStandardOrder: this.isStandardOrder,
        isShowTrustCheck: this.isShowTrustCheck,
        isEditing: false, // New location, not editing
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('result', result);
        this.handleLocationSelection(result);
      } else {
        // If dialog was closed without adding location, just refresh the list
        this.loadLocations();
      }
    });
  }

  onSelectLocation(location: any) {
    console.log('location', location);
    // Prevent selection if location is selected for other groups but not current group
    if (
      this.isLocationSelected(location.locationId) &&
      !this.isLocationSelectedForCurrentGroup(location.locationId)
    ) {
      return; // Disable clicking on locations selected for other groups
    }

    // If location is already selected for this groupIndex, allow editing
    const isCurrentlySelected = this.isLocationSelectedForCurrentGroup(
      location.locationId
    );

    const dialogRef = this._matDialog.open(AddNewLocationDialogComponent, {
      panelClass: ['commonDialogWrapper'],
      backdropClass: 'dialogBackdrop',
      autoFocus: false,
      width: '610px',
      data: {
        heading: this.isPickup ? 'Pickup Location' : 'Drop Off Location',
        isPickup: this.isPickup,
        location: location,
        selected: this.selected || null,
        groupIndex: this.groupIndex,
        isInterRegional: this.isInterRegional,
        isHeavyItemAndRelocateOrder: this.isHeavyItemAndRelocateOrder,
        isStandardOrder: this.isStandardOrder,
        isShowTrustCheck: this.isShowTrustCheck,
        isEditing: isCurrentlySelected, // Pass flag to indicate if editing existing selection
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.handleLocationSelection(result);
      } else {
        console.log('Dialog closed without changes', result);
        this.loadLocations(); // Refresh on cancel
      }
    });
  }

  handleLocationSelection(result: any) {
    // DEBUG START
    try {
      console.log('[PickupLocationsDialog] handleLocationSelection:enter', {
        isInterRegional: this.isInterRegional,
        isPickup: this.isPickup,
        groupIndex: this.groupIndex,
      });
      console.log('[PickupLocationsDialog] handleLocationSelection:input', {
        result,
      });
      if (this.isInterRegional && this.isPickup == 0) {
        const webDestinations = JSON.parse(
          localStorage.getItem('webDestinations') || '[]'
        );
        console.log(
          '[PickupLocationsDialog] handleLocationSelection:webDestinations:count',
          webDestinations?.length
        );
        const destination =
          webDestinations.find(
            (item: any) =>
              item.destination_name?.toLowerCase() ===
              result?.locality?.toLowerCase()
          ) ||
          webDestinations.find((dest) => {
            const addressParts = result?.address
              ?.split(',')
              .map((p) => p.trim().toLowerCase());
            return addressParts?.some(
              (part) => part === dest.destination_name.toLowerCase()
            );
          });
        console.log(
          '[PickupLocationsDialog] handleLocationSelection:destination:match',
          Boolean(destination)
        );
        if (destination) {
          console.log(
            '[PickupLocationsDialog] handleLocationSelection:emit:locationSelected_with_destination',
            destination
          );
          const payload = {
            ...result,
            destinationAddress: destination.destination_name,
          };
          console.log(
            '[PickupLocationsDialog] handleLocationSelection:emit:locationSelected_with_destination',
            payload
          );
          this.locationSelected.emit(payload);
          console.log(
            '[PickupLocationsDialog] handleLocationSelection:emit:close'
          );
          this.close.emit();
        } else {
          const lat = result?.lat ?? result?.latitude;
          const lng = result?.long ?? result?.lng ?? result?.longitude;
          console.log(
            '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:candidates',
            {
              lat: lat,
              lng: lng,
              placeId: result?.placeId || result?.placeID || result?.place_id,
            }
          );
          if (lat != null && lng != null) {
            this._auth
              .reverseGeoCode({ lat: lat, long: lng })
              .pipe(take(1))
              .subscribe({
                next: (res: any) => {
                  console.log(
                    '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:success',
                    res
                  );
                  const resultsArr = res?.data?.results || res?.results || [];
                  const target = String(
                    result?.placeId || result?.placeID || result?.place_id || ''
                  );
                  const foundObj = resultsArr.find(
                    (r: any) => String(r?.place_id) === target
                  );
                  const found = Boolean(foundObj);
                  console.log(
                    '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:place_match',
                    {
                      found: found,
                      target: target,
                      results: resultsArr?.length,
                    }
                  );
                  if (found) {
                    // Try to match address_components to a webDestination by regionName
                    console.log(
                      '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:address_components',
                      foundObj?.address_components
                    );
                    let matchedDestination: any = null;
                    try {
                      const addressComponents =
                        foundObj?.address_components || [];
                      const candidateNames: string[] = [];
                      addressComponents?.forEach((comp: any) => {
                        const longName = String(comp?.long_name || '')
                          .trim()
                          .toLowerCase();
                        const shortName = String(comp?.short_name || '')
                          .trim()
                          .toLowerCase();
                        if (longName) candidateNames.push(longName);
                        if (shortName) candidateNames.push(shortName);
                      });
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:candidateNames',
                        candidateNames
                      );
                      // const requiredDestinations = webDestinations.filter(
                      //   (dest: any) => {
                      //     const region = String(dest?.regionName || '')
                      //       .trim()
                      //       .toLowerCase();
                      //     return region && candidateNames.includes(region);
                      //   }
                      // );
                      matchedDestination = (webDestinations || []).find(
                        (dest: any) => {
                          const region = String(dest?.regionName || '')
                            .trim()
                            .toLowerCase();
                          return region && candidateNames.includes(region);
                        }
                      );

                      // matchedDestination = requiredDestinations.filter((item) =>
                      //   candidateNames.some((keyword) =>
                      //     item.destination_name
                      //       .toLowerCase()
                      //       .includes(keyword.toLowerCase())
                      //   )
                      // );
                      // console.log(
                      //   '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:bruhh',
                      //   filteredData
                      // );
                      // console.log(
                      //   '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:requiredDestinations',
                      //   requiredDestinations
                      // );
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:matchedDestination',
                        matchedDestination
                      );
                    } catch {}

                    if (matchedDestination?.destination_name) {
                      const payload = {
                        ...result,
                        destinationAddress: matchedDestination?.destination_name,
                      };
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:emit:locationSelected_with_destination_from_reverseGeocode',
                        payload
                      );
                      this.locationSelected.emit(payload);
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:emit:close'
                      );
                      this.close.emit();
                    } else {
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:openDialog:WebDestinationsDialogComponent:no_match'
                      );
                      const dialogRef = this._matDialog.open(
                        WebDestinationsDialogComponent,
                        {
                          panelClass: ['commonDialogWrapper'],
                          backdropClass: 'dialogBackdrop',
                          autoFocus: false,
                          width: '610px',
                          data: {
                            webDestinations: webDestinations,
                            location: result,
                          },
                        }
                      );
                      console.log(
                        '[PickupLocationsDialog] handleLocationSelection:emit:close'
                      );
                      this.close.emit();
                    }
                  } else {
                    console.log(
                      '[PickupLocationsDialog] handleLocationSelection:openDialog:WebDestinationsDialogComponent:no_match'
                    );
                    const dialogRef = this._matDialog.open(
                      WebDestinationsDialogComponent,
                      {
                        panelClass: ['commonDialogWrapper'],
                        backdropClass: 'dialogBackdrop',
                        autoFocus: false,
                        width: '610px',
                        data: {
                          webDestinations: webDestinations,
                          location: result,
                        },
                      }
                    );
                    console.log(
                      '[PickupLocationsDialog] handleLocationSelection:emit:close'
                    );
                    this.close.emit();
                  }
                },
                error: (err) => {
                  console.log(
                    '[PickupLocationsDialog] handleLocationSelection:reverseGeocode:error',
                    err
                  );
                  const dialogRef = this._matDialog.open(
                    WebDestinationsDialogComponent,
                    {
                      panelClass: ['commonDialogWrapper'],
                      backdropClass: 'dialogBackdrop',
                      autoFocus: false,
                      width: '610px',
                      data: {
                        webDestinations: webDestinations,
                        location: result,
                      },
                    }
                  );
                  console.log(
                    '[PickupLocationsDialog] handleLocationSelection:emit:close'
                  );
                  this.close.emit();
                },
              });
          } else {
            console.log(
              '[PickupLocationsDialog] handleLocationSelection:openDialog:WebDestinationsDialogComponent:no_coords'
            );
            const dialogRef = this._matDialog.open(
              WebDestinationsDialogComponent,
              {
                panelClass: ['commonDialogWrapper'],
                backdropClass: 'dialogBackdrop',
                autoFocus: false,
                width: '610px',
                data: { webDestinations: webDestinations, location: result },
              }
            );
            console.log(
              '[PickupLocationsDialog] handleLocationSelection:emit:close'
            );
            this.close.emit();
          }
        }
      } else {
        console.log(
          '[PickupLocationsDialog] handleLocationSelection:emit:locationSelected_passthrough',
          result
        );
        this.locationSelected.emit(result);
        console.log(
          '[PickupLocationsDialog] handleLocationSelection:emit:close'
        );
        this.close.emit();
      }
    } catch (err) {
      console.log(
        '[PickupLocationsDialog] handleLocationSelection:exception',
        err
      );
      try {
        console.log(
          '[PickupLocationsDialog] handleLocationSelection:emit:close_on_exception'
        );
        this.close.emit();
      } catch {}
    }
  }

  isLocationSelected(locationId: string): boolean {
    // Show all selected locations with borders
    return this.selectedDropoffs.includes(locationId);
  }

  isLocationSelectedForCurrentGroup(locationId: string): boolean {
    // Show "Selected" badge only for the current groupIndex being edited
    if (
      !this.isPickup &&
      this.groupIndex !== undefined &&
      this.groupIndex !== null
    ) {
      if (Array.isArray(this.selected)) {
        const specificDropoff = this.selected[this.groupIndex];
        return specificDropoff?.dropoffPoint?.locationId === locationId;
      }
    } else if (this.isPickup && this.selected?.locationId) {
      return this.selected.locationId === locationId;
    }
    return false;
  }

  locationIdChecker(id: string): boolean {
    // This method is used for showing/hiding delete button
    // Hide delete button for any selected location (regardless of groupIndex)
    return this.selectedDropoffs.includes(id);
  }

  onDeleteAddress(location: any) {
    const dialog = this._matDialog.open(ConfirmationDialogComponent, {
      panelClass: ['commonDialogWrapper'],
      backdropClass: 'dialogBackdrop',
      width: '480px',
      data: {
        heading: 'Alert!',
        headingColor: 'text-red',
        message: `Are you sure you want to delete this ${
          this.isPickup ? 'pickup' : 'delivery'
        } address?`,
        action: {
          ok: 'OK',
          cancel: 'Cancel',
          align: 'end',
        },
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.deleteAddress(location);
      }
    });
  }

  async deleteAddress(location: any) {
    try {
      await this.afs.collection(OrderLocations).doc(location.id).delete();
      this.loadLocations();
    } catch (err) {
      console.error('Delete failed', err);
    }
  }

  onClose() {
    this.close.emit();
  }

  ngOnDestroy(): void {
    this.selectedDropoffs = [];
  }
}
